import { CosmeticType, type CosmeticCatalogEntry, type UserLoadoutDTO, type WalletDTO } from '@push.io/shared';
import { readScopedToken, writeScopedToken } from '../session/storage.js';
import type {
  AccountApi,
  AccountSnapshot,
  CosmeticSlotKey,
  EquipCosmeticRequest,
  UnequipCosmeticRequest,
  WelcomeAccountPayload,
} from './types.js';

const INITIAL_SNAPSHOT: AccountSnapshot = {
  token: null,
  catalog: [],
  inventory: [],
  loadout: null,
  wallet: null,
  loading: false,
  error: null,
  savingSlots: [],
  account: null,
};

const SLOT_BY_TYPE: Partial<Record<CosmeticType, CosmeticSlotKey>> = {
  [CosmeticType.BaseColor]: 'baseColorId',
  [CosmeticType.Skin]: 'skinId',
  [CosmeticType.Pattern]: 'patternId',
  [CosmeticType.Flag]: 'flagId',
  [CosmeticType.Letter]: 'letterId',
  [CosmeticType.Accessory]: 'accessoryId',
  [CosmeticType.Trail]: 'trailId',
  [CosmeticType.Nameplate]: 'nameplateId',
  [CosmeticType.Badge]: 'badgeId',
  [CosmeticType.VictoryEffect]: 'victoryEffectId',
};

const OPTIONAL_SLOTS = new Set<CosmeticSlotKey>([
  'skinId', 'patternId', 'flagId', 'letterId', 'accessoryId',
  'trailId', 'nameplateId', 'badgeId', 'victoryEffectId',
]);

export type PurchaseErrorCode = 'not_found' | 'disabled' | 'already_owned' | 'insufficient_funds' | 'server_error';

export const PURCHASE_ERROR_MESSAGES: Record<PurchaseErrorCode, { es: string; en: string; fr: string }> = {
  insufficient_funds: {
    es: 'Monedas insuficientes', en: 'Insufficient coins', fr: 'Pièces insuffisantes',
  },
  already_owned: {
    es: 'Ya tienes este cosmético', en: 'You already own this cosmetic', fr: 'Vous possédez déjà ce cosmétique',
  },
  not_found: {
    es: 'Cosmético no encontrado', en: 'Cosmetic not found', fr: 'Cosmétique introuvable',
  },
  disabled: {
    es: 'Cosmético no disponible', en: 'Cosmetic not available', fr: 'Cosmétique non disponible',
  },
  server_error: {
    es: 'Error del servidor', en: 'Server error', fr: 'Erreur du serveur',
  },
};

export class AccountController {
  private snapshot: AccountSnapshot = { ...INITIAL_SNAPSHOT };
  private listeners = new Set<(snapshot: AccountSnapshot) => void>();

  constructor(private api: AccountApi) {
    this.snapshot.token = readToken();
    if (this.snapshot.token) {
      this.refreshAccount();
    }
  }

  subscribe(listener: (snapshot: AccountSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): AccountSnapshot {
    return {
      ...this.snapshot,
      catalog: [...this.snapshot.catalog],
      inventory: [...this.snapshot.inventory],
      savingSlots: [...this.snapshot.savingSlots],
    };
  }

  private async refreshAccount(): Promise<void> {
    try {
      const account = await this.api.getAccount();
      this.snapshot.account = account;
      this.emit();
    } catch {
      this.snapshot.account = null;
      this.emit();
    }
  }

  hydrateWelcome(payload: WelcomeAccountPayload): void {
    if (payload.token) {
      this.snapshot.token = payload.token;
      writeScopedToken(payload.token);
    }
    if (payload.cosmetics) this.snapshot.inventory = payload.cosmetics;
    if (payload.loadout !== undefined) this.snapshot.loadout = payload.loadout;
    if (payload.wallet) this.snapshot.wallet = payload.wallet;
    this.refreshAccount();
    this.emit();
  }

  async refreshAll(): Promise<void> {
    this.patch({ loading: true, error: null });
    try {
      const [catalog, inventory, loadout, wallet] = await Promise.all([
        this.api.getCatalog(),
        this.api.getInventory(),
        this.api.getLoadout(),
        this.api.getWallet(),
      ]);
      this.snapshot.catalog = catalog;
      this.snapshot.inventory = inventory;
      this.snapshot.loadout = loadout;
      this.snapshot.wallet = wallet;
      this.patch({ loading: false, error: null });
      await this.refreshAccount();
    } catch (error) {
      this.patch({ loading: false, error: toMessage(error) });
    }
  }

  async logout(): Promise<void> {
    try {
      await this.api.logout();
    } catch {
      // Continue with local cleanup even if server call fails
    }
    this.snapshot.token = null;
    this.snapshot.account = null;
    this.snapshot.catalog = [];
    this.snapshot.inventory = [];
    this.snapshot.loadout = null;
    this.snapshot.wallet = null;
    writeScopedToken('');
    this.emit();
  }

  get accountType(): string {
    return this.snapshot.account?.accountType || 'GUEST';
  }

  get isRegistered(): boolean {
    return this.snapshot.account?.accountType === 'REGISTERED';
  }

  get displayName(): string {
    return this.snapshot.account?.externalIdentity?.displayName
      || this.snapshot.account?.name
      || 'Invitado';
  }

  get avatarUrl(): string | null {
    return this.snapshot.account?.externalIdentity?.avatarUrl || null;
  }

  async updatePlayerName(name: string): Promise<void> {
    if (!this.isRegistered) return;
    const cleanName = name.trim().replace(/\s+/g, ' ').slice(0, 16);
    if (!cleanName || cleanName === this.snapshot.account?.name) return;
    this.snapshot.account = await this.api.updatePlayerName(cleanName);
    this.emit();
  }

  async grantAllCosmetics(): Promise<number> {
    const result = await this.api.grantAllCosmetics();
    this.snapshot.inventory = result.inventory;
    this.snapshot.loadout = result.loadout;
    this.patch({ error: null });
    return result.granted;
  }

  updateWalletFromServer(softCurrency: number): void {
    const prev = this.snapshot.wallet;
    this.snapshot.wallet = {
      userId: prev?.userId || '',
      softCurrency,
    };
    this.emit();
  }

  async purchase(cosmeticId: string): Promise<{ wallet: WalletDTO; loadout: UserLoadoutDTO }> {
    this.patch({ error: null });
    try {
      const result = await this.api.purchase(cosmeticId);
      this.snapshot.wallet = result.wallet;
      this.snapshot.inventory = [...this.snapshot.inventory, {
        cosmetic_id: cosmeticId,
        user_id: result.wallet.userId,
        source: 'soft_currency_purchase',
        source_reference: null,
        unlocked_at: new Date(),
        metadata: {},
      }];
      this.snapshot.loadout = result.loadout;
      this.emit();
      return result;
    } catch (error) {
      this.patch({ error: toMessage(error) });
      throw error;
    }
  }

  getCatalogForType(type: CosmeticType): CosmeticCatalogEntry[] {
    return this.snapshot.catalog.filter((item) => item.type === type);
  }

  isOwned(cosmeticId: string): boolean {
    return this.snapshot.inventory.some((entry) => entry.cosmetic_id === cosmeticId);
  }

  isEquipped(cosmeticId: string): boolean {
    if (!this.snapshot.loadout) return false;
    return Object.values(this.snapshot.loadout).includes(cosmeticId);
  }

  getEquippedCosmeticId(slot: CosmeticSlotKey): string | null {
    return this.snapshot.loadout?.[slot] ?? null;
  }

  canUnequip(slot: CosmeticSlotKey): boolean {
    return OPTIONAL_SLOTS.has(slot);
  }

  async equip(request: EquipCosmeticRequest): Promise<void> {
    const cosmetic = this.snapshot.catalog.find((item) => item.id === request.cosmeticId);
    if (!cosmetic) throw new Error('cosmetic_not_found');
    if (!this.isOwned(request.cosmeticId)) throw new Error('cosmetic_locked');
    const expectedSlot = SLOT_BY_TYPE[cosmetic.type];
    if (expectedSlot !== request.slot) throw new Error('invalid_slot');
    if (this.snapshot.savingSlots.includes(request.slot)) return;

    const previous = this.snapshot.loadout;
    this.optimisticEquip(request.slot, request.cosmeticId);
    this.setSaving(request.slot, true);
    try {
      this.snapshot.loadout = await this.api.equipCosmetic(request);
      this.patch({ error: null });
    } catch (error) {
      this.snapshot.loadout = previous;
      this.patch({ error: toMessage(error) });
      throw error;
    } finally {
      this.setSaving(request.slot, false);
    }
  }

  async unequip(request: UnequipCosmeticRequest): Promise<void> {
    if (!this.canUnequip(request.slot)) throw new Error('required_slot');
    if (this.snapshot.savingSlots.includes(request.slot)) return;

    const previous = this.snapshot.loadout;
    if (previous) {
      this.snapshot.loadout = { ...previous, [request.slot]: null };
      this.emit();
    }
    this.setSaving(request.slot, true);
    try {
      this.snapshot.loadout = await this.api.unequipCosmetic(request);
      this.patch({ error: null });
    } catch (error) {
      this.snapshot.loadout = previous;
      this.patch({ error: toMessage(error) });
      throw error;
    } finally {
      this.setSaving(request.slot, false);
    }
  }

  async setAccessoryColor(accessoryColor: string | null): Promise<void> {
    if (!this.snapshot.loadout?.accessoryId) return;
    const previous = this.snapshot.loadout;
    this.snapshot.loadout = { ...previous, accessoryColor };
    this.emit();
    try {
      this.snapshot.loadout = await this.api.setAccessoryColor(accessoryColor);
      this.patch({ error: null });
    } catch (error) {
      this.snapshot.loadout = previous;
      this.patch({ error: toMessage(error) });
      throw error;
    }
  }

  private optimisticEquip(slot: CosmeticSlotKey, cosmeticId: string): void {
    if (!this.snapshot.loadout) return;
    const next: UserLoadoutDTO = { ...this.snapshot.loadout, [slot]: cosmeticId };
    if (slot === 'patternId') next.flagId = null;
    if (slot === 'flagId') next.patternId = null;
    this.snapshot.loadout = next;
    this.emit();
  }

  private setSaving(slot: CosmeticSlotKey, saving: boolean): void {
    const set = new Set(this.snapshot.savingSlots);
    if (saving) set.add(slot);
    else set.delete(slot);
    this.snapshot.savingSlots = [...set];
    this.emit();
  }

  private patch(patch: Partial<AccountSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}

function readToken(): string | null {
  try {
    const g = globalThis as any;
    const params = new URLSearchParams(g.location?.search || '');
    const urlToken = params.get('token');
    if (urlToken) {
      writeScopedToken(urlToken);
      try {
        const cleanUrl = new URL(g.location.href);
        cleanUrl.searchParams.delete('token');
        cleanUrl.searchParams.delete('playerId');
        cleanUrl.searchParams.delete('accountType');
        cleanUrl.searchParams.delete('isNewLink');
        g.history.replaceState(null, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      } catch {
        // Authentication succeeded even if the address bar cannot be rewritten.
      }
      return urlToken;
    }
    const token = readScopedToken();
    if (token && token.length > 0) return token;
    return null;
  } catch {
    return null;
  }
}

function toMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'unknown_error';
}
